import { useState, useEffect, useRef, useCallback } from "react";
import { Button, Alert, Spinner, Badge, Form, Nav, ProgressBar } from "react-bootstrap";
import axios from "axios";
import Utils from "../../common/Utils";

const CqcAiChat = ({ cqcdocId, contentId, udid, title, settings, onComplete, onClose }) => {
  const [activeTab,    setActiveTab]    = useState('chat'); // 'chat' or 'upload'
  const [messages,     setMessages]     = useState([]);
  const [collected,    setCollected]    = useState({});
  const [inputText,    setInputText]    = useState('');
  const [loading,      setLoading]      = useState(false);
  const [parsing,      setParsing]      = useState(true);
  const [error,        setError]        = useState(null);
  const [ttsOn,        setTtsOn]        = useState(false);
  
  // Microphone & Speech Dictation States
  const [micOn,            setMicOn]            = useState(false);
  const [micReconnecting,  setMicReconnecting]  = useState(false);

  // Complete & Output States
  const [complete,     setComplete]     = useState(false);
  const [docHtml,      setDocHtml]      = useState(null);
  const [savedUdf,     setSavedUdf]     = useState(null);
  const [submitting,   setSubmitting]   = useState(false);
  const [generating,   setGenerating]   = useState(false);
  
  // Photo Upload States
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [expDate,      setExpDate]      = useState('');
  const [year,         setYear]         = useState(new Date().getFullYear());
  const [showExpiry,   setShowExpiry]   = useState(false);
  const [resumeAvailable, setResumeAvailable] = useState(null);
  const sessionKey = `cqc_session_${cqcdocId}_${contentId}`;

  const sessionIdRef   = useRef(null);
  const sendMessageRef = useRef(null);
  const recognRef      = useRef(null);
  const chatEndRef     = useRef(null);
  const inputRef       = useRef(null);
  const micOnRef       = useRef(false);
  const manualTextRef  = useRef('');
  const silenceTimerRef = useRef(null);
  const stopMicRef = useRef(null);

  // Keep ref updated
  useEffect(() => {
    micOnRef.current = micOn;
  }, [micOn]);

  // Scroll chat to bottom
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Determine if expiry is user-defined from settings
  useEffect(() => {
    if (settings) {
      const flag = parseInt(settings.flag ?? 0);
      setShowExpiry(flag === 3);
    }
  }, [settings]);

  // Setup Speech Recognition (Robust Dictation Mode)
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'en-GB';
    r.continuous = true;
    r.interimResults = true;
    
    r.onresult = e => {
      let finalSpeech = '';
      let interimSpeech = '';
      for (let i = e.resultIndex; i < e.results.length; ++i) {
        if (e.results[i].isFinal) {
          finalSpeech += e.results[i][0].transcript;
        } else {
          interimSpeech += e.results[i][0].transcript;
        }
      }

      if (finalSpeech) {
        finalSpeech = finalSpeech
          .replace(/\band over\b/gi, 'Andover')
          .replace(/\bsouth hampton\b/gi, 'Southampton')
          .replace(/\bwhit church\b/gi, 'Whitchurch')
          .replace(/\bsand banks\b/gi, 'Sandbanks');
      }

      const prefix = manualTextRef.current ? manualTextRef.current.trim() + ' ' : '';
      const textToSet = prefix + (finalSpeech + ' ' + interimSpeech).trim();
      setInputText(textToSet);
      
      if (finalSpeech) {
        manualTextRef.current = prefix + finalSpeech.trim();
      }

      // Auto-submit on silence detection
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      
      const currentText = textToSet.trim();
      if (currentText && e.results[e.results.length - 1].isFinal) {
        silenceTimerRef.current = setTimeout(() => {
          if (micOnRef.current) {
            stopMic();
            if (sendMessageRef.current) {
              sendMessageRef.current(currentText);
            }
          }
        }, 1800);
      }
    };

    r.onerror = (e) => {
      console.error("Speech Recognition Error:", e.error);
      micOnRef.current = false;
      setMicOn(false);
      if (e.error === 'not-allowed') {
        alert('Microphone permission blocked. Please allow microphone access for this website in your browser settings (and ensure you are using HTTPS).');
      } else if (e.error === 'service-not-allowed') {
        alert('Speech recognition is not supported or allowed in this browser / environment.');
      } else if (e.error) {
        alert('Microphone error: ' + e.error);
      }
    };
    
    r.onend = () => {
      if (micOnRef.current) {
        setMicReconnecting(true);
        setTimeout(() => {
          try {
            if (micOnRef.current) {
              recognRef.current?.start();
            }
          } catch(e) {}
          setMicReconnecting(false);
        }, 300);
      }
    };

    recognRef.current = r;
  }, []);

  // Keep sendMessageRef current on every render
  useEffect(() => {
    sendMessageRef.current = sendMessage;
    stopMicRef.current = stopMic;
  });

  // Start session or check for resume on mount
  useEffect(() => {
    const saved = localStorage.getItem(sessionKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const savedAt = new Date(parsed.savedAt);
        const hoursSince = (new Date() - savedAt) / (1000 * 60 * 60);
        if (hoursSince < 168) { // 7 days validity
          setResumeAvailable(parsed);
          setParsing(false);
          return;
        }
      } catch (e) {
        localStorage.removeItem(sessionKey);
      }
    }
    initSession();
    return () => {
      window.speechSynthesis?.cancel();
      try { recognRef.current?.stop(); } catch(e) {}
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
    };
  }, [cqcdocId, contentId]);

  // Handle tab switching cleanup (Cancel TTS and turn off mic)
  useEffect(() => {
    if (activeTab !== 'chat') {
      window.speechSynthesis?.cancel();
      stopMicRef.current?.();
    }
  }, [activeTab]);

  const initSession = async () => {
    setParsing(true); setError(null);
    try {
      const res = await axios.post(
        Utils.apiUrl('cqcs/ai_chat_start'),
        JSON.stringify({ cqcdoc_id: cqcdocId, content_id: contentId, year, exp_date: expDate }),
        { ...Utils.apiHeaders(), headers: { ...Utils.apiHeaders().headers, 'Content-Type': 'application/json' } }
      );
      const data = res.data;
      if (!data.success) throw new Error(data.message);
      sessionIdRef.current = data.session_id;

      // Save session info to local cache
      localStorage.setItem(sessionKey, JSON.stringify({
        sessionId: data.session_id,
        savedAt: new Date().toISOString()
      }));

      if (data.reply) {
        setMessages([{ role: 'assistant', content: data.reply }]);
        speak(data.reply);
      }
      setParsing(false);
    } catch (e) {
      setError('Could not start session: ' + e.message);
      setParsing(false);
    }
  };

  const handleResume = async () => {
    if (!resumeAvailable) return;
    setParsing(true);
    setError(null);
    try {
      const res = await axios.post(
        Utils.apiUrl('cqcs/ai_chat_resume'),
        JSON.stringify({ session_id: resumeAvailable.sessionId }),
        { ...Utils.apiHeaders(), headers: { ...Utils.apiHeaders().headers, 'Content-Type': 'application/json' } }
      );
      const data = res.data;
      if (!data.success) throw new Error(data.message);
      sessionIdRef.current = data.session_id;
      setMessages(data.messages || []);
      setCollected(data.collected || {});
      setResumeAvailable(null);
      setParsing(false);
    } catch (e) {
      alert('Could not resume session — starting fresh.');
      handleDiscardResume();
    }
  };

  const handleDiscardResume = () => {
    localStorage.removeItem(sessionKey);
    setResumeAvailable(null);
    initSession();
  };

  const sendMessage = useCallback(async (text = null) => {
    const msg = (text || inputText).trim();
    if (!msg || loading) return;
    if (!sessionIdRef.current) {
      setError('Session not ready — please wait a moment.');
      return;
    }
    
    // Stop mic dictation when sending message manually
    if (micOn) stopMic();

    setInputText('');
    manualTextRef.current = '';
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        Utils.apiUrl('cqcs/ai_chat'),
        JSON.stringify({ session_id: sessionIdRef.current, message: msg }),
        { ...Utils.apiHeaders(), headers: { ...Utils.apiHeaders().headers, 'Content-Type': 'application/json' } }
      );
      const data = res.data;
      setLoading(false);
      if (!data.success) { setError(data.message || 'Error. Try again.'); return; }
      if (data.new_fields) setCollected(prev => ({ ...prev, ...data.new_fields }));
      if (data.reply) { setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]); speak(data.reply); }
      if (data.complete) {
        setGenerating(true);
        try {
          const genRes = await axios.post(
            Utils.apiUrl('cqcs/ai_generate_document'),
            JSON.stringify({ session_id: sessionIdRef.current }),
            { ...Utils.apiHeaders(), headers: { ...Utils.apiHeaders().headers, 'Content-Type': 'application/json' } }
          );
          if (genRes.data.success) {
            setComplete(true);
            setDocHtml(genRes.data.document_html);
          } else {
            setError('Document generation failed: ' + genRes.data.message);
          }
        } catch (genErr) {
          setError('Document generation error: ' + (genErr.response?.data?.message || genErr.message));
        } finally {
          setGenerating(false);
        }
      }
    } catch (e) {
      setLoading(false);
      setError(e.response?.data?.message || e.message);
    } finally {
      inputRef.current?.focus();
    }
  }, [inputText, loading, micOn]);

  const handleSubmit = async () => {
    // If we already saved it (from image upload workflow), just return that udf!
    if (savedUdf) {
      if (onComplete) onComplete(savedUdf);
      return;
    }

    if (!docHtml) return;
    if (showExpiry && !expDate) { alert('Please set an expiry date.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        Utils.apiUrl('cqcs/ai_chat_submit'),
        JSON.stringify({ session_id: sessionIdRef.current || '', udid, year, exp_date: expDate, html: docHtml }),
        { ...Utils.apiHeaders(), headers: { ...Utils.apiHeaders().headers, 'Content-Type': 'application/json' } }
      );
      const data = res.data;
      if (data.success) {
        localStorage.removeItem(sessionKey);
        if (onComplete) onComplete(data.udf);
      } else {
        alert('Save failed: ' + data.message);
      }
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSubmitting(false); }
  };

  const speak = (text) => {
    if (activeTab !== 'chat') return;
    if (!ttsOn || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const clean = text.replace(/\*\*(.*?)\*\*/g, '$1').replace(/<[^>]+>/g, '').replace(/[✓✅⚠]/g, '');
    const utt   = new SpeechSynthesisUtterance(clean);
    utt.lang    = 'en-GB'; utt.rate = 0.93;
    const v = window.speechSynthesis.getVoices();
    const uv = v.find(x => x.lang === 'en-GB') || v.find(x => x.lang.startsWith('en'));
    if (uv) utt.voice = uv;
    window.speechSynthesis.speak(utt);
  };

  const toggleMic = () => {
    if (micOnRef.current) { stopMic(); return; }
    if (!recognRef.current) { alert('Voice input not supported. Use Chrome or Edge.'); return; }
    micOnRef.current = true;
    setMicOn(true);
    manualTextRef.current = inputText;
    try { recognRef.current.start(); } catch(e) {}
  };

  const stopMic = () => {
    micOnRef.current = false;
    setMicOn(false);
    setMicReconnecting(false);
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    try { recognRef.current?.stop(); } catch(e) {}
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxDim = 1000;
          let width = img.width;
          let height = img.height;
          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) {
              const cleanName = file.name.replace(/\.[^/.]+$/, "") + ".jpg";
              const compressedFile = new File([blob], cleanName, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          }, 'image/jpeg', 0.70);
        };
        img.onerror = () => resolve(file);
        img.src = event.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  // Image Upload Ingestion Workflow
  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const handleProcessImageUpload = async () => {
    if (selectedFiles.length === 0) return;
    if (showExpiry && !expDate) { alert('Please select an expiry date.'); return; }
    setLoading(true);
    setError(null);
    setUploadProgress(8);

    const processedFiles = [];
    try {
      for (let file of selectedFiles) {
        const comp = await compressImage(file);
        processedFiles.push(comp);
      }
    } catch (e) {
      processedFiles.push(...selectedFiles);
    }
    setUploadProgress(20);

    const fd = new FormData();
    processedFiles.forEach(file => {
      fd.append('images[]', file);
    });
    fd.append('cqcdoc_id', cqcdocId);
    fd.append('content_id', contentId);
    fd.append('udid', udid);
    fd.append('year', year);
    fd.append('exp_date', expDate);

    setUploadProgress(40);
    let isAsync = false;

    try {
      const res = await axios.post(
        Utils.apiUrl('cqcs/ai_ingest_image'),
        fd,
        {
          headers: {
            ...Utils.apiHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setUploadProgress(80);
      const data = res.data;
      if (data.success) {
        if (data.status === 'processing') {
          isAsync = true;
          const fileId = data.file_id;
          let attempts = 0;
          const interval = setInterval(async () => {
            attempts++;
            if (attempts > 90) { // Timeout after 180 seconds (3 mins)
              clearInterval(interval);
              setLoading(false);
              setUploadProgress(0);
              setError('Image analysis timed out on the server.');
              return;
            }
            setUploadProgress(Math.min(99, 80 + Math.floor(attempts * 0.2)));
            try {
              const pollRes = await axios.get(
                Utils.apiUrl('cqcs/poll_upload/' + fileId),
                Utils.apiHeaders()
              );
              const pollData = pollRes.data;
              if (pollData.status === 'complete') {
                clearInterval(interval);
                setUploadProgress(100);
                setDocHtml(pollData.html);
                setSavedUdf(null);
                setComplete(true);
                setLoading(false);
              } else if (pollData.status === 'failed') {
                clearInterval(interval);
                setLoading(false);
                setUploadProgress(0);
                setError('Image Ingestion Failed: ' + pollData.message);
              }
            } catch (e) {
              // Ignore transient connection issues
            }
          }, 2000);
        } else {
          setUploadProgress(100);
          setDocHtml(data.html);
          setSavedUdf(null);
          setComplete(true);
        }
      } else {
        throw new Error(data.message || 'Processing failed.');
      }
    } catch (e) {
      setError('Image Processing Error: ' + (e.response?.data?.message || e.message));
      setUploadProgress(0);
    } finally {
      if (!isAsync) {
        setLoading(false);
      }
    }
  };

  const collectedCount = Object.values(collected).filter(Boolean).length;

  return (
    <div className="cqc-ai-chat-container">
      <style>{`
        .cqc-ai-chat-container {
          display: flex;
          flex-direction: column;
          min-height: 480px;
          max-height: 85vh;
        }
        @media (max-width: 767px) {
          .cqc-ai-chat-container {
            min-height: 75vh !important;
            max-height: 95vh !important;
            padding: 5px;
          }
          .cqc-ai-chat-container .btn {
            min-height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 13px !important;
          }
          .cqc-ai-chat-container input, .cqc-ai-chat-container select {
            min-height: 44px !important;
            font-size: 13px !important;
          }
          .cqc-ai-chat-container .chat-messages-box {
            max-height: 38vh !important;
          }
          .cqc-ai-chat-container .nav-tabs .nav-link {
            padding: 8px 10px;
            font-size: 11px !important;
          }
          .cqc-ai-chat-container .evidence-uploader-btn {
            width: 100% !important;
          }
        }
      `}</style>
      
      {resumeAvailable ? (
        <div className="card shadow-sm border-warning p-4 m-3 text-center" style={{ borderRadius: 10 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⏸</div>
          <h5 className="fw-bold mb-2">Unfinished Document Session Detected</h5>
          <p className="text-muted small mb-4">
            You have an active progress session for this document saved on {new Date(resumeAvailable.savedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}.
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <Button variant="primary" onClick={handleResume} disabled={parsing}>
              {parsing ? <Spinner size="sm" className="me-1" /> : 'Resume Unfinished Session'}
            </Button>
            <Button variant="outline-secondary" onClick={handleDiscardResume} disabled={parsing}>
              Discard &amp; Start Fresh
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Expiry config on top */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #e9ecef', flexWrap: 'wrap' }}>
            <Form.Group style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Form.Label style={{ fontSize: 11, marginBottom: 0, whiteSpace: 'nowrap' }}>Audit Year</Form.Label>
              <Form.Select size="sm" value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ width: 85, fontSize: 11 }}>
                {[0,1,2].map(i => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
              </Form.Select>
            </Form.Group>
            {showExpiry && (
              <Form.Group style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Form.Label style={{ fontSize: 11, marginBottom: 0, whiteSpace: 'nowrap' }}>Expires On</Form.Label>
                <Form.Control type="date" size="sm" value={expDate} onChange={e => setExpDate(e.target.value)} style={{ width: 135, fontSize: 11 }} />
              </Form.Group>
            )}
          </div>

          {/* AI-assistance disclaimer — visible for both conversation and image-upload paths,
              before the user starts answering. The same notice is also embedded in the
              generated document itself once complete. */}
          {!complete && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 12px', marginBottom: 10, background: '#fff8e6', border: '1px solid #f0d896', borderLeft: '4px solid #b8860b', borderRadius: 6, fontSize: 11.5, color: '#5c4a1a', lineHeight: 1.4 }}>
              <span style={{ fontSize: 14 }}>⚠️</span>
              <span>
                <strong>AI-assisted draft.</strong> Whatever is produced here — by conversation or from an uploaded photo —
                is a starting point, not a finished record. A suitably qualified CQC Manager must review, correct where
                needed, and approve it before relying on it as compliance evidence.
              </span>
            </div>
          )}

          {/* Tabs */}
          {!complete && (
            <Nav variant="tabs" className="mb-2" activeKey={activeTab} onSelect={k => {
              setActiveTab(k);
              window.speechSynthesis?.cancel();
              if (k !== 'chat') {
                stopMicRef.current?.();
              }
            }}>
              <Nav.Item><Nav.Link eventKey="chat" style={{ fontSize: 12 }}>💬 Complete by conversation</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="upload" style={{ fontSize: 12 }}>📷 Upload completed paper form</Nav.Link></Nav.Item>
            </Nav>
          )}

          {/* Tab contents */}
          {!complete && activeTab === 'chat' && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              {/* Header info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', borderBottom: '1px solid #dee2e6', marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: 13 }}>✦ Dictate or Chat</strong>
                  <div style={{ fontSize: 11, color: '#6c757d', marginTop: 2 }}>
                    {parsing ? 'Preparing…' : collectedCount + ' fields collected'}
                  </div>
                </div>
              </div>

              {parsing ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : error ? (
                <div style={{ flex: 1, padding: 15 }}>
                  <Alert variant="danger">{error}</Alert>
                  <Button variant="outline-primary" size="sm" onClick={initSession}>Retry Connection</Button>
                </div>
              ) : (
                <>
                  {/* Messages */}
                  <div className="chat-messages-box" style={{ flex: 1, overflowY: 'auto', padding: '10px 5px', minHeight: 180, maxHeight: '50vh', borderBottom: '1px solid #e9ecef', marginBottom: 10 }}>
                    {messages.map((msg, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 12, justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                        {msg.role !== 'user' && (
                          <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#007bff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 'bold' }}>AI</div>
                        )}
                        <div style={{ 
                          maxWidth: '75%', 
                          background: msg.role === 'user' ? '#eef3ff' : '#f8f9fa', 
                          border: '1px solid', 
                          borderColor: msg.role === 'user' ? '#d0e0ff' : '#e9ecef', 
                          borderRadius: 8, 
                          padding: '8px 12px', 
                          fontSize: 12.5, 
                          lineHeight: 1.5,
                          whiteSpace: 'pre-line'
                        }} dangerouslySetInnerHTML={{ __html: msg.content }} />
                      </div>
                    ))}
                    {loading && (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#007bff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 'bold' }}>AI</div>
                        <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: '8px 12px' }}>
                          <Spinner animation="grow" size="sm" variant="secondary" />
                        </div>
                      </div>
                    )}
                    {generating && (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#28a745', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 'bold' }}>✓</div>
                        <div style={{ background: '#e8f7ec', border: '1px solid #c3e6cb', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                          <Spinner animation="border" size="sm" className="me-2" /> Compiling filled compliance layout...
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {micOn && <Alert variant="danger" className="py-1 px-2 mb-2 animate-pulse" style={{ fontSize: 12 }}>🎙 Listening — speak your answer clearly (continuous dictation mode)…</Alert>}
                  
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input ref={inputRef} type="text" value={inputText} onChange={e => { setInputText(e.target.value); manualTextRef.current = e.target.value; }}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                      placeholder="Type your answer or use dictation mic…" disabled={loading}
                      style={{ flex: 1, padding: '8px 11px', border: '1.5px solid #dee2e6', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }} />
                    
                    {/* TTS Toggle Button */}
                    <Button variant={ttsOn ? 'info' : 'outline-secondary'} onClick={() => { setTtsOn(!ttsOn); if (ttsOn) window.speechSynthesis?.cancel(); }} style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 38, borderRadius: 8 }} title={ttsOn ? "Disable TTS voice reader" : "Enable TTS voice reader"}>
                      {ttsOn ? '🔊' : '🔇'}
                    </Button>

                    {/* Microphone Dictation Button */}
                    <Button variant={micOn ? (micReconnecting ? 'warning' : 'danger') : 'outline-secondary'} onClick={toggleMic} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 38, borderRadius: 8 }} title={micOn ? "Stop Listening" : "Start Voice Input"}>
                      {micOn ? (micReconnecting ? 'Sync…' : '🎙') : '🎙'}
                    </Button>

                    <Button variant="primary" size="sm" onClick={() => sendMessage()} disabled={loading || !inputText.trim()} style={{ height: 38, borderRadius: 8, padding: '8px 16px', fontSize: 13 }}>Send</Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tab: Upload Photo of Paper Form */}
          {!complete && activeTab === 'upload' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '15px 0' }}>
              <Alert variant="info" style={{ fontSize: 12 }}>
                💡 <strong>Paper Form Scan Mode:</strong> Upload 1 or more photos/scans of your completed paper document. Claude Vision will extract all values, verify CQC compliance, and compile the final report.
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Select Photo/Scan Pages</Form.Label>
                <Form.Control type="file" multiple accept="image/*" onChange={handleFileChange} disabled={loading} style={{ fontSize: 13 }} />
              </Form.Group>

              {selectedFiles.length > 0 && (
                <div style={{ background: '#f8f9fa', border: '1px dashed #dee2e6', borderRadius: 6, padding: '10px 15px', marginBottom: 15, fontSize: 12 }}>
                  <strong>Files Selected ({selectedFiles.length}):</strong>
                  <ul className="mb-0 mt-1 pl-3">
                    {selectedFiles.map((file, i) => (
                      <li key={i}>{file.name} ({Math.round(file.size / 1024)} KB)</li>
                    ))}
                  </ul>
                </div>
              )}

              {error && <Alert variant="danger" className="py-2 px-3 mb-2" style={{ fontSize: 12 }}>{error}</Alert>}

              {uploadProgress > 0 && (
                <div className="mb-3">
                  <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 4 }}>Vision analysis status...</div>
                  <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} variant="success" style={{ height: 18, fontSize: 10 }} />
                </div>
              )}

              <div style={{ marginTop: 'auto', display: 'flex', gap: 10 }}>
                <Button variant="secondary" onClick={onClose} disabled={loading} style={{ fontSize: 12 }}>Cancel</Button>
                <Button variant="primary" onClick={handleProcessImageUpload} disabled={loading || selectedFiles.length === 0} style={{ fontSize: 12, flex: 1 }}>
                  {loading ? <><Spinner size="sm" className="me-1" />Extracting & Compiling…</> : '✦ Analyze Completed Photo'}
                </Button>
              </div>
            </div>
          )}

          {/* Preview + submit */}
          {complete && docHtml && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Alert variant="success" className="py-2 px-3 mb-2" style={{ fontSize: 12 }}>
                ✅ <strong>Document processed!</strong> Please review the generated document contents below.
              </Alert>

              <Alert variant="info" className="py-1 px-3 mb-2" style={{ fontSize: 11, background: '#e8f4fd', border: '1px solid #b8daff', color: '#004085' }}>
                ✍️ <strong>Tip:</strong> You can click inside the box below to edit and format the text directly before uploading!
              </Alert>

              <div 
                contentEditable={true}
                suppressContentEditableWarning={true}
                onBlur={(e) => setDocHtml(e.target.innerHTML)}
                style={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  padding: 16, 
                  background: '#fff', 
                  border: '2px dashed #28a745', 
                  borderRadius: 8, 
                  fontSize: 12, 
                  lineHeight: 1.8, 
                  fontFamily: 'Georgia,serif', 
                  marginBottom: 10,
                  outline: 'none'
                }}
                dangerouslySetInnerHTML={{ __html: docHtml }} 
              />

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', width: '100%', marginTop: 'auto' }}>
                {!savedUdf && (
                  <Button variant="outline-secondary" onClick={() => { setComplete(false); setDocHtml(null); }} style={{ flex: 1, minWidth: '130px', padding: '10px 14px', fontSize: '13px' }}>
                    ✏ Continue editing
                  </Button>
                )}
                <Button variant="success" onClick={handleSubmit} disabled={submitting} style={{ flex: 2, minWidth: '180px', padding: '10px 14px', fontSize: '14px', fontWeight: 'bold' }}>
                  {submitting ? <><Spinner size="sm" className="me-1" />Saving…</> : '✅ Upload to Denttrack'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CqcAiChat;
